/**
 * /app/influencers — agency influencer database (Task 041 §1).
 *
 * Wired against `list-influencers`, `get-influencer?id=`,
 * `list-influencer-platform-accounts?influencer_id=`. The catalog is
 * already org-scoped at the wrapper level (no `client_id` parameter),
 * so the agency page consumes the same endpoint as the client tab —
 * just without the client-scope banner and without client-shortlist
 * affordances.
 *
 * Mutation surfaces (add to catalog, edit, archive) remain deferred —
 * the only action button renders `.btn-coming-soon`.
 */

import { useEffect, useMemo, useState } from 'react';
import { isApiError } from '../api/client';
import { getInfluencer, listInfluencerPlatformAccounts, listInfluencers } from '../api/endpoints';
import type {
  InfluencerPlatform,
  InfluencerPlatformAccountRow,
  InfluencerRow,
  InfluencerStatus,
  InfluencerTier,
} from '../api/types';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../components/states';

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly influencers: ReadonlyArray<InfluencerRow> }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly influencer: InfluencerRow;
      readonly accounts: ReadonlyArray<InfluencerPlatformAccountRow>;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const TIER_FILTERS: ReadonlyArray<{
  readonly value: InfluencerTier | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'كل المستويات' },
  { value: 'nano', label: 'نانو' },
  { value: 'micro', label: 'مايكرو' },
  { value: 'mid_tier', label: 'متوسط' },
  { value: 'macro', label: 'ماكرو' },
  { value: 'mega', label: 'ميجا' },
  { value: 'celebrity', label: 'مشاهير' },
];

export function AgencyInfluencersPage() {
  const [tier, setTier] = useState<InfluencerTier | 'all'>('all');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listInfluencers({ signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', influencers: data.influencers });
        if (data.influencers.length > 0 && data.influencers[0]) {
          setSelectedId((prev) =>
            prev && data.influencers.some((i) => i.id === prev) ? prev : data.influencers[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    Promise.all([
      getInfluencer(selectedId, { signal: ctrl.signal }),
      listInfluencerPlatformAccounts(selectedId, { signal: ctrl.signal }),
    ])
      .then(([influencerData, accountsData]) => {
        if (ctrl.signal.aborted) return;
        setDetail({
          status: 'ready',
          influencer: influencerData.influencer,
          accounts: accountsData.platform_accounts,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedId]);

  const filtered = useMemo(() => {
    if (list.status !== 'ready') return [];
    return tier === 'all' ? list.influencers : list.influencers.filter((i) => i.tier === tier);
  }, [list, tier]);

  // Reconcile selection when the client-side tier filter narrows the list
  // past the currently selected row. Without this, switching tiers leaves
  // the detail panel showing an influencer no longer visible (Codex P2).
  useEffect(() => {
    if (list.status !== 'ready') return;
    if (filtered.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((i) => i.id === selectedId)) {
      setSelectedId(filtered[0]!.id);
    }
  }, [filtered, list.status, selectedId]);

  return (
    <div className="stack-md">
      <div>
        <h1 className="h-page">
          المؤثرون <em className="serif italic">على مستوى الوكالة</em>
        </h1>
        <p className="body" style={{ marginTop: 6, color: 'var(--ink-2)' }}>
          كتالوج org-wide. اختر مؤثراً لعرض ملفه الكامل والحسابات على المنصات.
        </p>
      </div>

      <div className="toolbar-row">
        {TIER_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={tier === f.value ? 'chip on' : 'chip'}
            onClick={() => setTier(f.value)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ marginInlineStart: 'auto' }}>
          <button type="button" className="btn-coming-soon" disabled>
            إضافة مؤثر (قريباً)
          </button>
        </span>
      </div>

      {list.status === 'loading' ? <LoadingState title="جاري تحميل كتالوج المؤثرين…" /> : null}
      {list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الكتالوج"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {list.status === 'ready' ? (
        list.influencers.length === 0 ? (
          <EmptyState
            title="لا يوجد مؤثرون في الكتالوج"
            description="ستظهر هنا قائمة المؤثرين بمجرد إضافتهم على مستوى الوكالة."
          />
        ) : filtered.length === 0 ? (
          <EmptyState title="لا نتائج" description="جرّب فلتر مستوى آخر." />
        ) : (
          <div className="workspace-split">
            <aside className="workspace-list">
              <div className="sec-title">
                <h2>المؤثرون</h2>
                <span className="meta">{filtered.length}</span>
              </div>
              <ul className="row-list">
                {filtered.map((i) => (
                  <li key={i.id}>
                    <button
                      type="button"
                      className={i.id === selectedId ? 'row-card row-card-active' : 'row-card'}
                      onClick={() => setSelectedId(i.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <strong style={{ fontSize: 13 }}>{i.display_name}</strong>
                        <span className={`pill pill-${tierVariant(i.tier)}`}>
                          {tierLabel(i.tier)}
                        </span>
                      </div>
                      <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                        {i.primary_country ?? '—'}
                        {i.primary_city ? ` · ${i.primary_city}` : ''}
                        {i.categories.length > 0 ? ` · ${i.categories.slice(0, 2).join('، ')}` : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
            <section className="workspace-detail">
              <InfluencerDetail state={detail} />
            </section>
          </div>
        )
      ) : null}
    </div>
  );
}

function InfluencerDetail({ state }: { readonly state: DetailState }) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر مؤثراً لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل بيانات المؤثر…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="المؤثر غير متاح" description="ربما تم أرشفته." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل بيانات المؤثر" error={state.error} />;

  const { influencer, accounts } = state;
  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{influencer.display_name}</h2>
          <span className={`pill pill-${influencerStatusVariant(influencer.status)}`}>
            {influencerStatusLabel(influencer.status)}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field label="المستوى" value={tierLabel(influencer.tier)} />
          <Field label="الاسم القانوني" value={influencer.legal_name} />
          <Field label="الدولة" value={influencer.primary_country} />
          <Field label="المدينة" value={influencer.primary_city} />
          <Field
            label="اللغات"
            value={influencer.languages.length > 0 ? influencer.languages.join('، ') : null}
          />
          <Field
            label="الفئات"
            value={influencer.categories.length > 0 ? influencer.categories.join('، ') : null}
          />
        </div>
        {influencer.public_bio ? (
          <p className="body" style={{ marginTop: 14, whiteSpace: 'pre-wrap' }}>
            {influencer.public_bio}
          </p>
        ) : null}
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الحسابات على المنصات</h2>
          <span className="meta">{accounts.length}</span>
        </div>
        {accounts.length === 0 ? (
          <EmptyState title="لا توجد حسابات منصات" description="ستظهر هنا الحسابات بمجرد ربطها." />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>المنصة</th>
                <th>المعرّف</th>
                <th className="num">المتابعون</th>
                <th className="num">متوسط المشاهدات</th>
                <th className="num">معدل التفاعل</th>
                <th>آخر تحديث</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="pill pill-info">{platformLabel(a.platform)}</span>
                  </td>
                  <td style={{ direction: 'ltr', textAlign: 'start' }}>
                    {a.profile_url ? (
                      <a href={a.profile_url} target="_blank" rel="noreferrer">
                        @{a.handle}
                      </a>
                    ) : (
                      <span>@{a.handle}</span>
                    )}
                  </td>
                  <td className="num">
                    {a.follower_count !== null ? a.follower_count.toLocaleString('ar-SA') : '—'}
                  </td>
                  <td className="num">
                    {a.average_views !== null ? a.average_views.toLocaleString('ar-SA') : '—'}
                  </td>
                  <td className="num">
                    {a.average_engagement_rate !== null
                      ? `${(a.average_engagement_rate * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td>
                    {a.last_refreshed_at
                      ? new Date(a.last_refreshed_at).toLocaleDateString('ar-SA')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function tierLabel(tier: InfluencerTier): string {
  switch (tier) {
    case 'nano':
      return 'نانو';
    case 'micro':
      return 'مايكرو';
    case 'mid_tier':
      return 'متوسط';
    case 'macro':
      return 'ماكرو';
    case 'mega':
      return 'ميجا';
    case 'celebrity':
      return 'مشاهير';
    case 'unknown':
      return 'غير محدد';
    default:
      return tier;
  }
}

function tierVariant(tier: InfluencerTier): string {
  switch (tier) {
    case 'celebrity':
    case 'mega':
      return 'review';
    case 'macro':
      return 'info';
    case 'mid_tier':
    case 'micro':
      return 'active';
    case 'nano':
      return 'paused';
    default:
      return 'draft';
  }
}

function influencerStatusLabel(status: InfluencerStatus): string {
  switch (status) {
    case 'active':
      return 'نشط';
    case 'paused':
      return 'متوقف';
    case 'blocked':
      return 'محظور';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

function influencerStatusVariant(status: InfluencerStatus): string {
  switch (status) {
    case 'active':
      return 'active';
    case 'paused':
      return 'paused';
    case 'blocked':
      return 'error';
    case 'archived':
      return 'archived';
    default:
      return 'info';
  }
}

function platformLabel(platform: InfluencerPlatform): string {
  switch (platform) {
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'snapchat':
      return 'Snapchat';
    case 'youtube':
      return 'YouTube';
    case 'x':
      return 'X';
    case 'linkedin':
      return 'LinkedIn';
    case 'other':
      return 'أخرى';
    default:
      return platform;
  }
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}
